import { Agent, request as undiciRequest, type Dispatcher } from 'undici';
import { discardBody } from '../security/body-limit';
import { createConnectOptions } from '../security/pinned-lookup';
import type { PerformRequest, PerformRequestInput, UpstreamResponse } from '../types';

interface UpstreamMessage {
  statusCode: number;
  headers: Record<string, string | string[] | undefined>;
  body: AsyncIterable<Uint8Array> & { destroy?: () => void };
}

export interface PinnedRequestOptions {
  method: 'GET';
  headers: Record<string, string>;
  signal: AbortSignal;
  dispatcher: Dispatcher;
  maxRedirections: number;
}

export type PinnedRequestImpl = (url: string, options: PinnedRequestOptions) => Promise<UpstreamMessage>;

export async function executePinnedGet(
  input: PerformRequestInput,
  deps: {
    requestImpl?: PinnedRequestImpl;
    connectOptions?: typeof createConnectOptions;
  } = {},
): Promise<UpstreamResponse> {
  const connectOptions = deps.connectOptions ?? createConnectOptions;
  const requestImpl = deps.requestImpl ?? (undiciRequest as unknown as PinnedRequestImpl);
  const agent = new Agent({
    connect: connectOptions(input.ip, input.url.hostname, input.timeoutMs),
    headersTimeout: input.timeoutMs,
    bodyTimeout: input.timeoutMs,
    maxHeaderSize: 16 * 1024,
    connections: 1,
  });

  try {
    const response = await requestImpl(input.url.href, {
      method: 'GET',
      headers: {
        'user-agent': input.userAgent,
        accept: 'text/html,application/xhtml+xml,application/xml;q=0.9,*/*;q=0.8',
        'accept-language': 'en',
        'accept-encoding': 'identity',
        'cache-control': 'no-cache',
      },
      signal: input.signal,
      dispatcher: agent,
      maxRedirections: 0,
    });

    let truncated = false;
    try {
      const result = await discardBody(response.body, input.maxBodyBytes);
      truncated = result.truncated;
    } finally {
      response.body.destroy?.();
    }

    const headers: Record<string, string | string[]> = {};
    for (const [key, value] of Object.entries(response.headers)) {
      if (typeof value === 'string' || Array.isArray(value)) headers[key] = value;
    }

    return {
      statusCode: response.statusCode,
      headers,
      truncated,
    };
  } finally {
    await agent.close();
  }
}

export const performPinnedRequest: PerformRequest = (input) => executePinnedGet(input);
